/**
 * The read-only email row at the bottom of the edit-profile form. It renders
 * last, after `Phone number`, because the design frame draws `Email` as the
 * last field (see ISSUE-487's plan, section R.1). A null email is a real
 * state for an Apple-only or Google-only account (owner answer Q181).
 * Adding or changing the email lives on the Sign-in methods screen, so the
 * link opens its email step, `/settings/sign-in-methods?step=add-email`
 * (ISSUE-1023). Its method list hides Change when email is the only linked
 * method, which is why the link skips the list.
 */
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { SIGN_IN_METHODS_HEADING } from "../signInMethods/signInMethodsScreenLogic";
import {
  EMAIL_ABSENT_NOTE,
  EMAIL_ABSENT_VALUE,
  EMAIL_LABEL,
  EMAIL_PRESENT_NOTE,
  SIGN_IN_METHODS_ROUTE,
} from "./editProfileLogic";

export const EmailBlock = ({
  email,
}: {
  /** null for an Apple-only or Google-only account (owner answer Q181). */
  email: string | null;
}): ReactNode => {
  return (
    <div data-testid="edit-profile-email" className="flex flex-col gap-1">
      <span className="text-caption text-ink-tertiary">{EMAIL_LABEL}</span>
      {email === null ? (
        <>
          <span className="text-body text-ink-tertiary">
            {EMAIL_ABSENT_VALUE}
          </span>
          <span className="text-caption text-ink-secondary">
            {EMAIL_ABSENT_NOTE.split(SIGN_IN_METHODS_HEADING)[0]}
            <Link
              to={SIGN_IN_METHODS_ROUTE}
              search={{ step: "add-email" }}
              className="text-primary underline underline-offset-4"
              data-testid="edit-profile-email-link"
            >
              {SIGN_IN_METHODS_HEADING}
            </Link>
            .
          </span>
        </>
      ) : (
        <>
          <span className="text-body text-ink">{email}</span>
          <span className="text-caption text-ink-secondary">
            {EMAIL_PRESENT_NOTE.split(SIGN_IN_METHODS_HEADING)[0]}
            <Link
              to={SIGN_IN_METHODS_ROUTE}
              search={{ step: "add-email" }}
              className="text-primary underline underline-offset-4"
              data-testid="edit-profile-email-link"
            >
              {SIGN_IN_METHODS_HEADING}
            </Link>
            .
          </span>
        </>
      )}
    </div>
  );
};
